import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, History, AlertCircle } from "lucide-react";
import { messageLogService, EMessageLogStatus } from "@/services/messageLog";
import { billingRuleService } from "@/services/billingRule";

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString("es-CO", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_LABEL: Record<EMessageLogStatus, { label: string; className: string }> = {
  [EMessageLogStatus.SENT]: {
    label: "Enviado",
    className: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  },
  [EMessageLogStatus.FAILED]: {
    label: "Fallido",
    className: "bg-destructive/15 text-destructive border-destructive/30",
  },
};

const PAGE_SIZE = 20;

export function MessageLogsTab() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ruleFilter, setRuleFilter] = useState<string>("all");

  const { data: rulesData } = useQuery({
    queryKey: ["billing-rules-filter-options"],
    queryFn: () => billingRuleService.getAll({ limit: 100 }),
  });
  const rules = rulesData?.data ?? [];

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["message-logs", statusFilter, ruleFilter],
    queryFn: ({ pageParam }) =>
      messageLogService.getAll({
        page: pageParam,
        limit: PAGE_SIZE,
        status: statusFilter !== "all" ? (statusFilter as EMessageLogStatus) : undefined,
        ruleId: ruleFilter !== "all" ? Number(ruleFilter) : undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { pageIndex, totalPages } = lastPage.pagination;
      return pageIndex < totalPages ? pageIndex + 1 : undefined;
    },
  });

  const logs = data?.pages.flatMap((p) => p.data) ?? [];

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value={EMessageLogStatus.SENT}>Enviado</SelectItem>
            <SelectItem value={EMessageLogStatus.FAILED}>Fallido</SelectItem>
          </SelectContent>
        </Select>

        <Select value={ruleFilter} onValueChange={setRuleFilter}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Regla" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las reglas</SelectItem>
            {rules.map((rule) => (
              <SelectItem key={rule.id} value={String(rule.id)}>
                {rule.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <History className="w-8 h-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">Sin mensajes registrados</p>
        </div>
      ) : (
        <div ref={scrollContainerRef} className="max-h-[480px] overflow-y-auto rounded-lg border border-border">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Plantilla</TableHead>
                <TableHead>Regla</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => {
                const statusInfo = STATUS_LABEL[log.status] ?? {
                  label: log.status,
                  className: "bg-muted text-muted-foreground border-border",
                };
                return (
                  <TableRow key={log.id} className="hover:bg-muted/30">
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                      {formatDateTime(log.sentAt)}
                    </TableCell>
                    <TableCell className="text-foreground text-sm">
                      {log.clientId ? (
                        <Link
                          to={`/clients/${log.clientId}`}
                          className="hover:underline hover:text-primary"
                        >
                          {log.client?.name ?? `#${log.clientId}`}
                        </Link>
                      ) : (
                        log.client?.name ?? "—"
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {log.template?.name ?? (log.rule?.templateId ? `#${log.rule.templateId}` : "—")}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {log.rule?.name ?? (log.ruleId ? `#${log.ruleId}` : "Manual")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className={statusInfo.className}>
                          {statusInfo.label}
                        </Badge>
                        {log.status === EMessageLogStatus.FAILED && log.errorMessage && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <AlertCircle className="w-3.5 h-3.5 text-destructive" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[260px] text-xs">
                              {log.errorMessage}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div ref={sentinelRef} className="flex items-center justify-center py-3">
            {isFetchingNextPage && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          </div>
        </div>
      )}
    </div>
  );
}
